import React, { useEffect, useRef, useState } from "react";
import { Send, Image as ImageIcon, ChevronDown, Smile } from "lucide-react";
import { useChatWindow } from "./useChatWindow";
import { getAssetUrl } from "../../services/helper";

const EMOJI_LIST = [
  "😀", "😂", "😍", "😊", "😉", "😎", "🤔", "😢", "😭", "😡",
  "👍", "👎", "👏", "🙏", "💪", "🔥", "🎉", "❤️", "💯", "✅",
  "😴", "🤗", "😅", "🙄", "😱", "🤝", "👀", "💀", "✨", "🥳",
];

function EmojiPicker({ onSelect, onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const handleEsc = (e) => e.key === "Escape" && onClose();
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="absolute bottom-14 left-0 z-20 bg-white border border-gray-200 rounded-2xl shadow-lg p-3 w-64"
    >
      <div className="grid grid-cols-6 gap-1">
        {EMOJI_LIST.map((emoji) => (
          <button
            key={emoji}
            type="button"
            onClick={() => onSelect(emoji)}
            className="text-xl p-1.5 rounded-lg hover:bg-gray-100"
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  );
}

function AvatarSmall({ name, avatar }) {
  return (
    <div
      className={`w-8 h-8 sm:w-[34px] sm:h-[34px] rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center`}

    >
      <img
        src={getAssetUrl(avatar)}
        alt={name}
        className="w-full h-full object-cover rounded-full"
      />
    </div>
  );
}

const ChatWindow = ({ chat, config, onBack, onEdit, messagesByChat, setMessagesByChat, isGroupOwner }) => {
  const win = useChatWindow({
    chat,
    ...config,
    messagesByChat,
    setMessagesByChat,
    isGroupOwner
  });
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const inputRef = useRef(null);
  // console.log({ chat });
  const handleEmojiSelect = (emoji) => {
    // Insert at cursor position rather than always appending to the end
    const el = inputRef.current;
    if (el && typeof el.selectionStart === "number") {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const next = win.messageInput.slice(0, start) + emoji + win.messageInput.slice(end);
      win.setMessageInput(next);
      requestAnimationFrame(() => {
        el.focus();
        el.selectionStart = el.selectionEnd = start + emoji.length;
      });
    } else {
      win.setMessageInput(win.messageInput + emoji);
    }
  };
  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-4 border-b flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="sm:hidden mr-2">←</button>
          <img src={getAssetUrl(chat.chat_avatar)} alt="" className="w-12 h-12 rounded-full" />
          <p className="font-semibold">{chat.chat_name}</p>
        </div>
        {chat.chat_type === "room" && win.isGroupOwner && (
          <button onClick={onEdit} className="text-sm font-medium px-4 py-1 border rounded-lg">
            Manage
          </button>
        )}
      </div>

      {/* === IMPROVED MESSAGE AREA === */}
      <div className="flex-1 flex flex-col min-h-0 border-t border-gray-100 relative bg-white">
        {/* Scrollable Messages Container */}
        <div
          ref={win.messagesContainerRef}
          className={`flex-1 min-h-0 overflow-y-auto p-6 space-y-6 custom-scrollbar scroll-smooth  ${chat.chat_type}-${chat.chat_id}`}
          style={{
            scrollBehavior: "smooth",
            height: win.attachments.length > 0
              ? "calc(100vh - 340px)"   // More space taken when attachments shown
              : "calc(100vh - 260px)",  // Normal height
            maxHeight: win.attachments.length > 0
              ? "calc(100vh - 340px)"
              : "calc(100vh - 260px)",
          }}
        >
          {win.isLoadingMore && (
            <div className="text-center py-4">
              <div className="inline-flex items-center gap-2 text-xs text-gray-400">
                <div className="w-3.5 h-3.5 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
                Loading older messages...
              </div>
            </div>
          )}

          {win.messagesLoading && win.messagesList.length === 0 && (
            <div className="flex justify-center py-12">
              <div className="w-6 h-6 border-2 border-[#1F3A5F] border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {win.messagesList.map((msg) => {
            const isSender = msg.isSender === 1 || msg.is_sender === 1 || msg.is_sender;
            //  console.log({isSender});
            return (
              <div key={msg.id} className={`flex ${isSender ? "justify-end" : "justify-start"} mb-4`}>
                <div className={`max-w-[80%] ${isSender ? "items-end" : "items-start"} flex flex-col`}>

                  {/* Sender Avatar (for received messages) */}
                  {!isSender && msg.sender_avatar && (
                    <div className="flex items-center gap-2 mb-1">
                      <AvatarSmall name={msg.sender_name} avatar={msg.sender_avatar} />
                      <span className="text-xs text-gray-500">{msg.sender_name}</span>
                    </div>
                  )}

                  <div
                    className={`px-4 py-3 rounded-3xl shadow-sm ${isSender
                      ? "bg-[#1F3A5F] text-white rounded-br-none"
                      : "bg-gray-100 text-gray-800 rounded-bl-none"
                      }`}
                  >
                    {/* Media Section */}
                    {msg.media_urls && msg.media_urls !== "[]" && (
                      <div className="mb-3 space-y-2">
                        {JSON.parse(msg.media_urls).map((url, i) => {
                          const fullUrl = getAssetUrl(url);
                          const isImage = /\.(jpe?g|png|gif|webp|jfif|avif|svg)$/i.test(fullUrl);
                          const isVideo = /\.(mp4|mov|webm|ogg)$/i.test(fullUrl);

                          return (
                            <div
                              key={i}
                              className="relative group cursor-pointer rounded-xl overflow-hidden"
                              onClick={() => win.openImageModal(fullUrl)}                        >
                              {isImage ? (
                                <img
                                  src={fullUrl}
                                  alt="media"
                                  className="max-h-64 w-auto max-w-full rounded-xl object-contain"
                                />
                              ) : isVideo ? (
                                <video
                                  src={fullUrl}
                                  controls
                                  className="max-h-64 w-auto max-w-full rounded-xl"
                                />
                              ) : (
                                <div className="bg-white p-4 rounded-xl border flex items-center gap-3">
                                  <div className="text-4xl">📎</div>
                                  <div className="truncate">
                                    <p className="font-medium text-sm">{url.split('/').pop()}</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Message Text */}
                    {msg.message && (
                      <p className="whitespace-pre-wrap leading-relaxed break-words">
                        {msg.message}
                      </p>
                    )}

                    {/* Timestamp */}
                    <span className={`text-[10px] block mt-1.5 opacity-70 ${isSender ? "text-right" : "text-left"}`}>
                      {new Date(msg.created_at).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit"
                      })}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}

          <div ref={win.messagesEndRef} />
        </div>

        {/* Scroll to Bottom Button */}
        {win.showScrollToBottom && (
          <button
            onClick={() => win.scrollToBottom("smooth")}
            className="absolute bottom-6 right-6 w-9 h-9 rounded-full bg-white shadow-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-all z-10"
          >
            <ChevronDown size={18} color="#1F3A60" />
            {win.unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px] bg-[#1F3A5F] text-white rounded-full flex items-center justify-center">
                {win.unreadCount}
              </span>
            )}
          </button>
        )}
      </div>
      {/* Improved Attachments Preview */}
      {win.attachments.length > 0 && (
        <div className="flex flex-wrap gap-3 mb-4 p-3 bg-gray-50 rounded-2xl border border-gray-100">
          {win.attachments.map((file, i) => (
            <div
              key={i}
              className="group relative flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2 pr-8 max-w-[220px] hover:shadow-sm transition-all"
            >
              {/* Icon based on file type */}
              <div className="text-[#1F3A5F]">
                {file.type.startsWith("image/") ? "🖼️" : "📄"}
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-700 truncate">{file.name}</p>
                <p className="text-[10px] text-gray-400">
                  {(file.size / 1024).toFixed(1)} KB
                </p>
              </div>

              {/* Clear Button - Always Visible on Hover & Better Position */}
              <button
                onClick={() => win.removeAttachment(i)}
                className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded-full text-lg leading-none shadow-sm transition-all opacity-100 group-hover:scale-110"
                title="Remove attachment"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      {/* Input Area */}
      <div className="border-t p-4 bg-white flex-shrink-0 relative">
        {showEmojiPicker && (
          <EmojiPicker
            onSelect={handleEmojiSelect}
            onClose={() => setShowEmojiPicker(false)}
          />
        )}

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setShowEmojiPicker((v) => !v)}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <Smile size={24} />
          </button>

          <label htmlFor={`chat-attachment-${chat.chat_id}`} className="cursor-pointer p-2 hover:bg-gray-100 rounded-full">
            <ImageIcon size={24} />
          </label>
          <input
            type="file"
            id={`chat-attachment-${chat.chat_id}`}
            multiple
            accept="image/*,video/*"
            className="hidden"
            onChange={win.handleFileSelect}
          />

          <input
            ref={inputRef}
            value={win.messageInput}
            onChange={(e) => win.setMessageInput(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && win.handleSend()}
            placeholder="Type your message..."
            className="flex-1 px-5 py-3 bg-gray-100 rounded-full focus:outline-none text-[15px]"
          />

          <button
            onClick={win.handleSend}
            disabled={win.isSending}
            className="bg-[#1F3A5F] text-white p-3 rounded-full disabled:opacity-70"
          >
            <Send size={20} />
          </button>
        </div>
      </div>
      {win.selectedImage && (
        <div
          className="fixed inset-0 z-[100
          00] bg-black/10 flex items-center justify-center p-4"
          onClick={win.closeImageModal}
        >
          <div className="relative max-w-5xl max-h-[92vh] rounded-3xl overflow-hidden shadow-2xl">
            <img
              src={getAssetUrl(win.selectedImage)}
              alt="Full view"
              className="h-50 w-50 w-auto rounded-3xl"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWindow;